import { UI, HStack, Stack, VStack } from '@genial/components';
import { BoxRadius, Spacing } from '@genial/design-tokens';
import { Pressable } from 'native-base';
import React from 'react';

import { InputMoney } from '../../../../../../../../../components/InputMoney';
import { RiskRating } from '../../../../../../../../../components/RiskRating';
import { TableContent } from '../../../../../../../../../components/TableContent';
import { useGlobalAssetContext } from '../../../../../contexts';
import { useCoesAssetContext } from '../../../../../contexts/products/Coes';
import { useCoesAssetController } from '../../../../../hooks/products/Coes';
import { AssetTitleWithSubtitle } from '../../../components/AssetTitleWithSubtitle';
import { AssetValueWithVariation } from '../../../components/AssetValueWithVariation';
import { LateralBar } from '../../../components/LateralBar';
import { ListLoadingItemDesktop } from '../ListLoadingItem/desktop';

import { CoesListItemTypes } from '.';

export const CoesListItemDesktop: React.FC<CoesListItemTypes.Props> = ({ offer }) => {
  const id = offer.productId;
  const {
    control,
    currentValue,
    handleInputBlur,
    handleInputClear,
    handleInputFocus,
    handleSubmit,
    hasError,
    helpText,
    isFocused,
    productsDetailText,
    currentValueFormatted,
  } = useCoesAssetController(offer);
  const { setModalOpen, setOfferSelected } = useCoesAssetContext();
  const { infoGetter, selectedTab } = useGlobalAssetContext();

  const completeOffer = { ...offer, orderValue: currentValue };

  const { Button, isSelected, forceLoading } = infoGetter('coes', completeOffer, 'productId');

  const onOpenDetailsModal = () => {
    setModalOpen(true);
    setOfferSelected(offer);
  };

  const infos = [
    {
      label: 'Risco',
      value: (
        <RiskRating
          currentRiskRating={offer.riskRatingScoreNormalized}
          tooltip={offer.riskRatingNormalized}
        />
      ),
    },
    {
      label: 'Vencimento',
      value: <UI.Text color="neutral.darkest">{offer.maturityDateNormalized}</UI.Text>,
    },
    ...offer.additionalInfoNormalized.map((info) => ({
      label: info.labelNormalized,
      value: <AssetValueWithVariation value={info.valueNormalized} variation={info.variation} />,
    })),
  ];

  if (forceLoading) {
    return <ListLoadingItemDesktop />;
  }

  return (
    <TableContent
      direction="row"
      space={0}
      p={0}
      px={Spacing['inline.xxxs']}
      h="96px"
      isHighlighted={isSelected}>
      <LateralBar
        backgroundColor={selectedTab.color}
        borderTopLeftRadius={BoxRadius.sm}
        borderBottomLeftRadius={BoxRadius.sm}
      />
      <HStack justifyContent="space-between" alignItems="center" w="full">
        <HStack space={Spacing['inline.xxxs']} alignItems="center">
          <UI.Tooltip testID="details-tooltip" label={productsDetailText}>
            <Pressable onPress={onOpenDetailsModal}>
              <UI.Icon name="Info" size="xs" />
            </Pressable>
          </UI.Tooltip>
          <Stack w="150px">
            <AssetTitleWithSubtitle
              title={offer.productNameNormalized}
              subtitle={offer.strategyNormalized}
            />
          </Stack>
        </HStack>

        {infos.map(({ label, value }) => (
          <VStack space={Spacing['stack.quarck']} key={label}>
            <UI.Text color="neutral.black" variant="semibold">
              {label}
            </UI.Text>
            {value}
          </VStack>
        ))}

        <VStack space={Spacing['stack.quarck']} maxW="200px" w="full">
          <UI.Text color="neutral.black" variant="semibold">
            Valor a aplicar
          </UI.Text>
          <InputMoney
            id={`coes-input${id}`}
            name="createAppliedAmount"
            control={control}
            handleFocus={handleInputFocus}
            handleBlur={handleInputBlur}
            handleSubmit={handleSubmit}
            handleClear={handleInputClear}
            isDisabled={isSelected}
            currentValue={currentValueFormatted}
            hasFocus={isFocused}
            minValue={offer.minimumApplication}
            customInputProps={{
              ...{ ...(isFocused && hasError && { error: hasError }) },
              ...{ ...(isFocused && !hasError && { helpText }) },
            }}
          />
        </VStack>

        <Button
          id={selectedTab.typeGetterId}
          offer={completeOffer}
          isDisabled={typeof hasError?.message === 'string'}
          productContextConstant={selectedTab}
        />
      </HStack>
    </TableContent>
  );
};
